import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
    Plus, 
    Edit2, 
    Trash2, 
    Calendar,
    Clock,
    MapPin,
    Users,
    UserPlus,
    Check,
    X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsPage = () => {
    const [events, setEvents] = useState([]);
    const [members, setMembers] = useState([]);
    const [rsvps, setRsvps] = useState({});
    const [loading, setLoading] = useState(true);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isRsvpModalOpen, setIsRsvpModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, eventId: null, eventTitle: '' });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: '',
        time_start: '',
        time_end: '',
        location: '',
        max_capacity: '',
        cover_image: ''
    });

    useEffect(() => {
        fetchEvents();
        fetchMembers();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${API}/events`);
            setEvents(response.data);
            // Fetch RSVPs for each event
            for (const event of response.data) {
                fetchEventRsvps(event.id);
            }
        } catch (error) {
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to load members');
        }
    };

    const fetchEventRsvps = async (eventId) => {
        try {
            const response = await axios.get(`${API}/rsvp/event/${eventId}`);
            setRsvps(prev => ({ ...prev, [eventId]: response.data }));
        } catch (error) {
            console.error('Failed to load RSVPs');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null
            };

            if (editingEvent) {
                await axios.put(`${API}/events/${editingEvent.id}`, payload);
                toast.success('Event updated successfully');
            } else {
                await axios.post(`${API}/events`, payload);
                toast.success('Event created successfully');
            }
            setIsEventModalOpen(false);
            resetForm();
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save event');
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            description: event.description || '',
            date: event.date,
            time_start: event.time_start,
            time_end: event.time_end || '',
            location: event.location || '',
            max_capacity: event.max_capacity?.toString() || '',
            cover_image: event.cover_image || ''
        });
        setIsEventModalOpen(true);
    };

    const handleDelete = async (eventId) => {
        try {
            await axios.delete(`${API}/events/${eventId}`);
            toast.success('Event deleted successfully');
            setDeleteConfirm({ open: false, eventId: null, eventTitle: '' });
            fetchEvents();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete event');
        }
    };

    const openDeleteConfirm = (event) => {
        setDeleteConfirm({ open: true, eventId: event.id, eventTitle: event.title });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({ open: false, eventId: null, eventTitle: '' });
    };

    const handleAddRsvp = async (memberId) => {
        if (!selectedEvent) return;
        try {
            await axios.post(`${API}/rsvp`, {
                event_id: selectedEvent.id,
                member_id: memberId,
                status: 'confirmed'
            });
            toast.success('RSVP added');
            fetchEventRsvps(selectedEvent.id);
            fetchEvents();
        } catch (error) {
            toast.error('Failed to add RSVP');
        }
    };

    const handleMarkAttended = async (memberId) => {
        if (!selectedEvent) return;
        try {
            await axios.post(`${API}/attendance/record`, {
                event_id: selectedEvent.id,
                member_id: memberId,
                attended: true
            });
            toast.success('Attendance recorded');
            fetchEventRsvps(selectedEvent.id);
        } catch (error) {
            toast.error('Failed to record attendance');
        }
    };

    const handleRemoveRsvp = async (rsvpId) => {
        try {
            await axios.delete(`${API}/rsvp/${rsvpId}`);
            toast.success('RSVP removed');
            if (selectedEvent) {
                fetchEventRsvps(selectedEvent.id);
                fetchEvents();
            }
        } catch (error) {
            toast.error('Failed to remove RSVP');
        }
    };

    const resetForm = () => {
        setEditingEvent(null);
        setFormData({
            title: '',
            description: '',
            date: '',
            time_start: '',
            time_end: '',
            location: '',
            max_capacity: '',
            cover_image: ''
        });
    };

    const openRsvpModal = (event) => {
        setSelectedEvent(event);
        setIsRsvpModalOpen(true);
    };

    const getMemberName = (memberId) => {
        const member = members.find(m => m.id === memberId);
        return member?.name || 'Unknown';
    };

    const getEventRsvps = (eventId) => {
        return rsvps[eventId] || [];
    };

    const getRsvpProgress = (event) => {
        if (!event.max_capacity) return 0;
        return (event.rsvp_count / event.max_capacity) * 100;
    };

    const getNonRsvpMembers = () => {
        if (!selectedEvent) return members;
        const rsvpMemberIds = getEventRsvps(selectedEvent.id).map(r => r.member_id);
        return members.filter(m => !rsvpMemberIds.includes(m.id));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading events...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="events-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                        Events & RSVP
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Manage R&R Saturdays gatherings and RSVPs
                    </p>
                </div>
                <Dialog open={isEventModalOpen} onOpenChange={(open) => {
                    setIsEventModalOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            data-testid="add-event-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Event
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                                {editingEvent ? 'Edit Event' : 'Create New Event'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Event Title *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                    placeholder="R&R Saturdays - April 2026"
                                    data-testid="event-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Join us for fellowship and connection..."
                                    rows={3}
                                    data-testid="event-description-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="date">Date *</Label>
                                    <Input
                                        id="date"
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        required
                                        data-testid="event-date-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="max_capacity">Max Capacity</Label>
                                    <Input
                                        id="max_capacity"
                                        type="number"
                                        value={formData.max_capacity}
                                        onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                                        placeholder="30"
                                        data-testid="event-capacity-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="time_start">Start Time *</Label>
                                    <Input
                                        id="time_start"
                                        type="time"
                                        value={formData.time_start}
                                        onChange={(e) => setFormData({ ...formData, time_start: e.target.value })}
                                        required
                                        data-testid="event-start-time-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="time_end">End Time</Label>
                                    <Input
                                        id="time_end"
                                        type="time"
                                        value={formData.time_end}
                                        onChange={(e) => setFormData({ ...formData, time_end: e.target.value })}
                                        data-testid="event-end-time-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="location">Location</Label>
                                <Input
                                    id="location"
                                    value={formData.location}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    placeholder="Community Center, Washington D.C."
                                    data-testid="event-location-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cover_image">Cover Image URL</Label>
                                <Input
                                    id="cover_image"
                                    value={formData.cover_image}
                                    onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                                    placeholder="https://..."
                                    data-testid="event-cover-input"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsEventModalOpen(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit"
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                    data-testid="save-event-btn"
                                >
                                    {editingEvent ? 'Update Event' : 'Create Event'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* RSVP Modal */}
            <Dialog open={isRsvpModalOpen} onOpenChange={setIsRsvpModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                            Manage RSVPs - {selectedEvent?.title}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-4 space-y-6">
                        {/* Current RSVPs */}
                        <div>
                            <h3 className="font-semibold text-white mb-3">
                                Current RSVPs ({getEventRsvps(selectedEvent?.id).length})
                            </h3>
                            {getEventRsvps(selectedEvent?.id).length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {getEventRsvps(selectedEvent?.id).map((rsvp) => (
                                        <div 
                                            key={rsvp.id}
                                            className="flex items-center justify-between p-3 bg-[#3D4435] rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-[#C9A227] flex items-center justify-center text-white text-sm font-medium">
                                                    {getMemberName(rsvp.member_id)?.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="font-medium text-white">
                                                    {getMemberName(rsvp.member_id)}
                                                </span>
                                                <Badge className={
                                                    rsvp.status === 'attended' 
                                                        ? 'bg-[#4A6B53] text-white' 
                                                        : 'bg-[#C48A54] text-white'
                                                }>
                                                    {rsvp.status}
                                                </Badge>
                                            </div>
                                            <div className="flex gap-2">
                                                {rsvp.status !== 'attended' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleMarkAttended(rsvp.member_id)}
                                                        className="text-[#4A6B53] border-[#4A6B53]"
                                                        data-testid={`mark-attended-${rsvp.id}`}
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleRemoveRsvp(rsvp.id)}
                                                    className="text-[#A64444] border-[#A64444]"
                                                    data-testid={`remove-rsvp-${rsvp.id}`}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[#A0A0A0] text-center py-4">No RSVPs yet</p>
                            )}
                        </div>

                        {/* Add RSVP */}
                        <div>
                            <h3 className="font-semibold text-white mb-3">Add RSVP</h3>
                            {getNonRsvpMembers().length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {getNonRsvpMembers().map((member) => (
                                        <div 
                                            key={member.id}
                                            className="flex items-center justify-between p-3 bg-[#4A5240] border border-[#5A6350] rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-[#3E4A3D] flex items-center justify-center text-white text-sm font-medium">
                                                    {member.name?.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-white">{member.name}</span>
                                            </div>
                                            <Button
                                                size="sm"
                                                onClick={() => handleAddRsvp(member.id)}
                                                className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                                data-testid={`add-rsvp-${member.id}`}
                                            >
                                                <UserPlus className="w-4 h-4 mr-1" />
                                                Add
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[#A0A0A0] text-center py-4">
                                    {members.length === 0 ? 'No members available' : 'All members have RSVP\'d'}
                                </p>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Events Grid */}
            {events.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                    {events.map((event) => (
                        <Card 
                            key={event.id} 
                            className="border-[#5A6350] shadow-sm card-hover overflow-hidden"
                            data-testid={`event-card-${event.id}`}
                        >
                            {/* Cover Image */}
                            <div 
                                className="h-40 bg-cover bg-center"
                                style={{
                                    backgroundImage: event.cover_image 
                                        ? `url(${event.cover_image})` 
                                        : 'url(https://images.pexels.com/photos/4262177/pexels-photo-4262177.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
                                }}
                            >
                                <div className="w-full h-full bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                                    <Badge className={event.is_active ? 'bg-[#4A6B53]' : 'bg-[#6E6862]'}>
                                        {event.is_active ? 'Active' : 'Past'}
                                    </Badge>
                                </div>
                            </div>

                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-3">
                                    <h3 className="font-semibold text-lg text-white line-clamp-1">
                                        {event.title}
                                    </h3>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => handleEdit(event)}
                                            className="p-1.5 hover:bg-[#3D4435] rounded-lg transition-colors"
                                            data-testid={`edit-event-${event.id}`}
                                        >
                                            <Edit2 className="w-4 h-4 text-[#A0A0A0]" />
                                        </button>
                                        <button
                                            onClick={() => openDeleteConfirm(event)}
                                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                            data-testid={`delete-event-${event.id}`}
                                        >
                                            <Trash2 className="w-4 h-4 text-[#A64444]" />
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm text-[#A0A0A0] mb-4">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span>{new Date(event.date).toLocaleDateString('en-US', { 
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        <span>{event.time_start}{event.time_end ? ` - ${event.time_end}` : ''}</span>
                                    </div>
                                    {event.location && (
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            <span className="line-clamp-1">{event.location}</span>
                                        </div>
                                    )}
                                </div>

                                {/* RSVP Progress */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between text-sm mb-2">
                                        <span className="text-[#A0A0A0]">RSVPs</span>
                                        <span className="font-medium text-white">
                                            {event.rsvp_count}{event.max_capacity ? ` / ${event.max_capacity}` : ''}
                                        </span>
                                    </div>
                                    {event.max_capacity && (
                                        <Progress 
                                            value={getRsvpProgress(event)} 
                                            className="h-2 bg-[#E6E1D8]"
                                        />
                                    )}
                                </div>

                                <Button
                                    onClick={() => openRsvpModal(event)}
                                    variant="outline"
                                    className="w-full border-[#845642] text-[#C9A227] hover:bg-[#C9A227] hover:text-white"
                                    data-testid={`manage-rsvp-${event.id}`}
                                >
                                    <Users className="w-4 h-4 mr-2" />
                                    Manage RSVPs
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card className="border-[#5A6350]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Calendar className="w-16 h-16 text-[#E6E1D8] mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No events yet</h3>
                        <p className="text-[#A0A0A0] text-center mb-4">
                            Create your first R&R Saturdays gathering
                        </p>
                        <Button 
                            onClick={() => setIsEventModalOpen(true)}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Event
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && closeDeleteConfirm()}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Playfair_Display']">
                            Delete Event
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-[#A0A0A0]">
                            Are you sure you want to delete <span className="text-white font-semibold">{deleteConfirm.eventTitle}</span>? 
                            This will also remove all RSVPs. This action cannot be undone.
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={closeDeleteConfirm}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                            data-testid="cancel-delete-event-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleDelete(deleteConfirm.eventId)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-event-btn"
                        >
                            Delete Event
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EventsPage;
